import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminApi, authApi } from '../utils/api';
import { clearBusinessContext, migrateGuestBusinessContextToAuthenticated } from '../utils/businessContext';
import { clearReadinessProfile, migrateGuestReadinessToAuthenticated } from '../utils/readinessStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hydrateSession = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await authApi.getCurrentUser();
        const currentUser = response?.data || null;
        if (!currentUser) {
          throw new Error('No user payload returned');
        }

        localStorage.setItem('user', JSON.stringify(currentUser));
        setUser(currentUser);
      } catch (error) {
        // Only logout if token is definitively invalid (401, 403 errors)
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          // For network errors, restore from localStorage to maintain session
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
            } catch (e) {
              setUser(null);
            }
          }
        }
      } finally {
        setLoading(false);
      }
    };

    hydrateSession();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await adminApi.login({ identifier: email, email, password });
      const { access_token, user: userData } = response.data;

      localStorage.setItem('authToken', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      migrateGuestBusinessContextToAuthenticated();
      migrateGuestReadinessToAuthenticated();
      setUser(userData);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const loginPublic = async (email, password) => {
    try {
      const response = await authApi.loginPublic({ identifier: email, password });
      const { access_token, user: userData } = response.data;

      localStorage.setItem('authToken', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      migrateGuestBusinessContextToAuthenticated();
      migrateGuestReadinessToAuthenticated();
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const loginPublicWithGoogle = async (credential) => {
    try {
      const response = await authApi.loginPublicWithGoogle({ credential });
      const { access_token, user: userData } = response.data;

      localStorage.setItem('authToken', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      migrateGuestBusinessContextToAuthenticated();
      migrateGuestReadinessToAuthenticated();
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Google login failed'
      };
    }
  };

  const registerPublic = async ({ email, password, fullName, username, roleProfile, companySize, newsletterOptIn = true }) => {
    try {
      const response = await authApi.registerPublic({
        email,
        password,
        full_name: fullName,
        username: username || null,
        role_profile: roleProfile || null,
        company_size: companySize || null,
        subscribe_newsletter: Boolean(newsletterOptIn),
      });
      const { access_token, user: userData, requires_confirmation: requiresConfirmation, message } = response.data || {};

      if (access_token && userData) {
        localStorage.setItem('authToken', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        migrateGuestBusinessContextToAuthenticated();
        migrateGuestReadinessToAuthenticated();
        setUser(userData);
      }

      return { success: true, user: userData || null, requiresConfirmation: Boolean(requiresConfirmation), message: message || '' };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    clearBusinessContext(true);
    clearReadinessProfile(true);
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    loginPublic,
    loginPublicWithGoogle,
    registerPublic,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
